/**
 * Event Booking (piece 4): an Expanded calendar pop-out and a Condensed list
 * row, chosen by available space. Six fields render in a fixed order that
 * branches only on eventType; the LOCATION field filters by what the event is
 * tagged to; the Condensed row expands in place, shows read-only fields, and
 * caps its Notes preview at the token's two lines.
 */
import { calendarDayInZone } from '../zonedTime';

export type EventType = 'Physical' | 'Online' | 'Phone' | 'PTO' | 'WorkOrderBlock';

export type EventBookingVariant = 'expanded' | 'condensed';

export const EB_NARROW_BREAKPOINT = 432;
export const EB_NOTES_MAX_LINES = 2;

/** The Condensed variant's own sub-breakpoint: narrow drops the Notes preview, wide shows it. */
export function condensedIsNarrow(availableWidth: number, breakpoint = EB_NARROW_BREAKPOINT): boolean {
  return availableWidth < breakpoint;
}

export type EventField = 'subject' | 'when' | 'location' | 'meetingLink' | 'callNumber' | 'attendees' | 'notes' | 'owner' | 'taggedRecord';

/** The type-specific field: LOCATION on a Physical event, the meeting link on an Online one, the call number on a Phone one. */
export function placeField(eventType: EventType): EventField {
  return eventType === 'Online' ? 'meetingLink' : eventType === 'Phone' ? 'callNumber' : 'location';
}

/**
 * The Expanded card's fields in the specification's own order (figure 22):
 * Date / Time first and enlarged, then Subject, Notes, Attendees, the Tagged
 * Record where there is one, and the type-specific field last; only that last
 * field branches on the type. 💰 The order read subject, when, place,
 * attendees, owner, notes until 2026-09-10, which was not the figure's.
 */
export function fieldOrder(eventType: EventType): EventField[] {
  return ['when', 'subject', 'notes', 'attendees', placeField(eventType)];
}

/** The Expanded card names what the event is tagged to, after the attendees, where it is tagged to anything (piece 12, figure 68). */
export function withTaggedRecord(fields: readonly EventField[], hasTag: boolean): EventField[] {
  if (!hasTag) return [...fields];
  const at = fields.indexOf('attendees');
  return [...fields.slice(0, at + 1), 'taggedRecord', ...fields.slice(at + 1)];
}

export type LocationScope = 'taggedCompanyOnly' | 'allPrioritiseTaggedCompany';

const HARD_FILTER_TAGS = new Set(['WorkOrder', 'Invoice', 'Bill']);

/** Tagged to a Work Order, Invoice or Bill the field offers that document's Company's Locations only; elsewhere it searches all and sorts the tagged Company's first. */
export function locationScope(taggedRecordType: string | null | undefined): LocationScope {
  return taggedRecordType && HARD_FILTER_TAGS.has(taggedRecordType) ? 'taggedCompanyOnly' : 'allPrioritiseTaggedCompany';
}

/** The Site Requirements band renders for a physical event only, and only when the resolver answers something. */
export function showsSiteRequirements(eventType: EventType, requirements: readonly unknown[] | null | undefined): boolean {
  return eventType !== 'Online' && eventType !== 'Phone' && !!requirements && requirements.length > 0;
}

/** Scenario B6: an event whose end falls on a later calendar day in its zone puts the end date on its own line. */
export function crossesMidnight(startsAt: Date, endsAt: Date | null, timeZone: string): boolean {
  if (!endsAt) return false;
  return calendarDayInZone(startsAt, timeZone) !== calendarDayInZone(endsAt, timeZone);
}

export interface CondensedRowState {
  expanded: boolean;
}

/** The right-side control expands the row in place and fires no action. */
export function toggleCondensedRow(state: CondensedRowState): CondensedRowState {
  return { expanded: !state.expanded };
}
